import type { IBasicInfoRow } from '@/Services/CampaignManagement/LineItemSummary.service';
import { Button, Table, TableBody, TableCell, TableRow, tableCellClasses } from '@mui/material';
import { memo, useState } from 'react';
import InfoTableModal from './InfoTableModal';
import SectionWrapper from './SectionWrapper';

export const rowspanTableMaxRows = 25;

interface IProps {
  rows: IBasicInfoRow[];
  caption: string;
  maxRows?: number;
}

const InfoTable = memo((props: IProps) => {
  const { rows, caption, maxRows } = props;
  const [isModalOpen, setIsModalOpen] = useState(false);

  const normalizedMaxRows = Math.max(0, maxRows ?? rows.length);
  const rowsCount = Math.min(rows.length, normalizedMaxRows);
  const shownRows = rows.slice(0, rowsCount);
  const hiddenRowCount = rows.length - rowsCount;

  const handleOpenModal = () => setIsModalOpen(true);
  const handleCloseModal = () => setIsModalOpen(false);

  return (
    <>
      <SectionWrapper>
        <Table
          size='small'
          sx={{
            '& caption': { captionSide: 'top', paddingY: '8px' },
            [`& tr:last-child .${tableCellClasses.root}`]: {
              border: 'none',
            },
          }}
        >
          <caption>{caption}</caption>
          <TableBody>
            {shownRows.map(({ value, label, rowSpan, thSx, tdSx }, key) => (
              <TableRow key={key}>
                {label ? (
                  <TableCell
                    rowSpan={
                      rowSpan
                        ? Math.min(rowSpan, rowsCount - key) + (hiddenRowCount && rowSpan > rowsCount - key ? 1 : 0)
                        : 1
                    }
                    sx={{ fontWeight: 600, ...thSx }}
                    component='th'
                    scope='row'
                  >
                    {label}
                  </TableCell>
                ) : null}
                <TableCell sx={{ ...tdSx }}>{value}</TableCell>
              </TableRow>
            ))}
            {!!hiddenRowCount && (
              <TableRow>
                <TableCell sx={{ border: 'none' }}>
                  <span>...+{hiddenRowCount} more</span>
                  <Button
                    variant='text'
                    size='small'
                    onClick={handleOpenModal}
                    sx={{ displayPrint: 'none', marginLeft: '8px' }}
                  >
                    View All
                  </Button>
                </TableCell>
              </TableRow>
            )}
          </TableBody>
        </Table>
      </SectionWrapper>

      {!!hiddenRowCount && (
        <InfoTableModal open={isModalOpen} onClose={handleCloseModal} rows={rows} caption={caption} />
      )}
    </>
  );
});

export default InfoTable;
